import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Routes as AppRoutes } from "../../../routes";
import VaFileInput from "../../../components/va/VaFileInput";
import Button from "../../../components/shared/buttons/button";
import Loader from "../../../components/shared/loader/Loader";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import { useParseInvoice } from "../../../hooks/useParseInvoice";
import {
  saveParseResult,
  getParseResult,
  saveOriginalPoFile,
  clearOriginalPoFile,
  saveInvoiceFile,
} from "../../../helpers/vaParseStorage";
import { PRESCRIPTION_UPLOAD_ACCEPT } from "../../../helpers/prescriptionFormat";
import "../../../styles/vaOrderModal.css";

export default function CreateOrderUploadPage({ asModal = false, onClose }) {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [originalPoFile, setOriginalPoFile] = useState(null);
  const [invoiceError, setInvoiceError] = useState("");
  const [originalPoError, setOriginalPoError] = useState("");
  const [parseError, setParseError] = useState("");
  const { parse, isUploading, reset } = useParseInvoice();

  const handleClose = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate(AppRoutes.Orders.path);
  };

  const handleContinue = async () => {
    if (!invoiceFile) return;
    setParseError("");

    try {
      const data = await parse(invoiceFile);
      saveParseResult(data.parseId, data);
      saveInvoiceFile(data.parseId, invoiceFile);

      if (originalPoFile) {
        saveOriginalPoFile(data.parseId, originalPoFile);
      } else {
        clearOriginalPoFile(data.parseId);
      }

      const reviewData = getParseResult(data.parseId) || data;
      const reviewParams = {
        create: "review",
        parseId: data.parseId,
      };
      if (asModal) {
        setSearchParams(reviewParams);
      } else {
        navigate(
          `${AppRoutes.CreateOrderReview.path}?parseId=${encodeURIComponent(data.parseId)}`,
          { state: { parse: reviewData } }
        );
      }
    } catch (err) {
      const msg =
        err?.data?.message ||
        err?.message ||
        "Failed to parse invoice. Try again.";
      setParseError(msg);
      reset();
    }
  };

  const busy = isUploading;
  const loadingLabel = "Reading invoice…";

  const content = (
    <div className="va-upload-form">
      {busy && (
        <div className="va-invoice-loading text-center py-4">
          <Loader />
          <p className="mb-0 mt-3 fw-medium">{loadingLabel}</p>
          <p className="text-muted small mb-0">This may take up to a minute</p>
        </div>
      )}

      {!busy && (
        <>
          <VaFileInput
            id="va-invoice-upload"
            label="Lovell PO File"
            labelClassName="va-review-bold-label"
            required
            file={invoiceFile}
            onFileSelect={(f, err) => {
              setInvoiceFile(f);
              setInvoiceError(err || "");
              setParseError("");
            }}
            error={invoiceError || parseError}
          />

          <VaFileInput
            id="va-po-upload"
            label="Original VA PO"
            labelClassName="va-review-bold-label"
            optional
            allowImages
            accept={PRESCRIPTION_UPLOAD_ACCEPT}
            file={originalPoFile}
            emptyLabel="Optional. PDF, PNG, or JPEG."
            onFileSelect={(f, err) => {
              setOriginalPoFile(f);
              setOriginalPoError(err || "");
            }}
            error={originalPoError}
          />
        </>
      )}
    </div>
  );

  if (asModal) {
    return (
      <CommonModal
        show
        setShow={(open) => {
          if (!open) handleClose();
        }}
        title="Upload PO from VA"
        className="modal-sm"
        centered
        closeOnBackdrop={false}
        onConfirm={handleContinue}
        onCancel={handleClose}
        confirmLoading={busy}
        confirmDisabled={!invoiceFile || busy}
        cancelDisabled={busy}
      >
        {content}
      </CommonModal>
    );
  }

  return (
    <div className="admin-page-card mx-auto" style={{ maxWidth: 560 }}>
      <h4 className="mb-3">Upload PO from VA</h4>
      {content}
      <div className="va-upload-form__actions">
        <Button
          label="Confirm"
          size="lg"
          onClick={handleContinue}
          disabled={!invoiceFile || busy}
          loading={busy}
        />
        <Button
          label="Cancel"
          size="lg"
          className="cancel"
          onClick={handleClose}
          disabled={busy}
        />
      </div>
    </div>
  );
}
